import React, { useEffect, useMemo, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import "../styles/AdminAttendance.css";
import { encodePathSegment } from "../utils/apiPath";

/**
 * AdminAttendancePage (Admin -> Obecności)
 * - UI na mock danych (backend podepniesz później)
 * - Layout zgodny z Twoim Kalendarzem:
 *   <AdminSidebar />
 *   <div className="main-content">
 *     <div className="attendance-page-inner"> ... </div>
 *   </div>
 */

const ATTENDANCE = {
    PRESENT: "present",
    ABSENT: "absent",
    LATE: "late",
    EXCUSED: "excused",
};

const ATTENDANCE_LABEL = {
    [ATTENDANCE.PRESENT]: "Obecny",
    [ATTENDANCE.ABSENT]: "Nieobecny",
    [ATTENDANCE.LATE]: "Spóźniony",
    [ATTENDANCE.EXCUSED]: "Usprawiedl.",
};

function pad2(n) {
    return String(n).padStart(2, "0");
}

function toDateInputValue(d) {
    const yyyy = d.getFullYear();
    const mm = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    return `${yyyy}-${mm}-${dd}`;
}

function fmtDate(iso) {
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
}

function downloadTextFile(filename, content, mime = "text/plain") {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function escapeCsv(v) {
    const s = String(v ?? "");
    if (/[",\n;]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
    return s;
}

function buildCsv(rows) {
    return rows.map((r) => r.map(escapeCsv).join(";")).join("\n");
}

export default function AdminAttendancePage() {
    // State for API data
    const [courses, setCourses] = useState([]);
    const [groups, setGroups] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

    const [courseId, setCourseId] = useState("");
    const [groupId, setGroupId] = useState("");

    const [fromDate, setFromDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return toDateInputValue(d);
    });
    const [toDate, setToDate] = useState(() => toDateInputValue(new Date()));

    const [sessionQuery, setSessionQuery] = useState("");
    const [studentQuery, setStudentQuery] = useState("");

    const groupsForCourse = useMemo(() => groups, [groups]);
    
    const studentsForGroup = useMemo(() => students, [students]);

    const [attendanceBySession, setAttendanceBySession] = useState({});
    const [selectedSessionId, setSelectedSessionId] = useState("");

    // Fetch courses/modules on mount
    useEffect(() => {
        async function fetchCourses() {
            try {
                setLoading(true);
                // Fetch modules directly
                const response = await fetch(`${process.env.REACT_APP_API_URL}/getData/modules`);
                if (!response.ok) throw new Error('Failed to fetch modules');
                const data = await response.json();
                // Transform to match expected format
                const transformed = data.map((m) => ({
                    id: String(m.module_id),
                    name: m.module_name,
                }));
                setCourses(transformed);
                if (transformed.length > 0 && !courseId) {
                    setCourseId(String(transformed[0].id));
                }
            } catch (error) {
                console.error('Error fetching courses:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch groups when course changes (anuluj stare odpowiedzi — szybka zmiana przedmiotu)
    useEffect(() => {
        let cancelled = false;

        async function fetchGroups() {
            if (!courseId) {
                setGroups([]);
                setGroupId("");
                return;
            }
            try {
                setLoading(true);
                const moduleId = parseInt(courseId, 10);
                if (isNaN(moduleId)) {
                    throw new Error('Invalid module ID');
                }
                const response = await fetch(`${process.env.REACT_APP_API_URL}/courses/modules/${moduleId}/groups`);
                if (!response.ok) throw new Error('Failed to fetch groups');
                const data = await response.json();
                if (cancelled) return;

                const seen = new Set();
                const transformed = [];
                for (const g of data) {
                    const id = String(g.group_code ?? "").trim();
                    if (!id || seen.has(id)) continue;
                    seen.add(id);
                    transformed.push({
                        id,
                        name: g.group_name,
                        city: g.group_name,
                    });
                }

                setGroups(transformed);

                // Zawsze trzymaj groupId zgodny z listą dla TEGO przedmiotu (stary kod po zmianie kursu = błąd w <select>)
                setGroupId((prev) => {
                    const prevStr = String(prev ?? "").trim();
                    if (prevStr && transformed.some((x) => x.id === prevStr)) return prevStr;
                    return transformed[0]?.id ?? "";
                });
            } catch (error) {
                if (cancelled) return;
                console.error('Error fetching groups:', error);
                setGroups([]);
                setGroupId("");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchGroups();
        return () => {
            cancelled = true;
        };
    }, [courseId]);

    function handleCourseChange(nextCourseId) {
        setCourseId(nextCourseId);
        setGroupId("");
        setGroups([]);
        setSessions([]);
        setSelectedSessionId("");
    }

    // Function to refresh sessions data
    const refreshSessions = async () => {
        if (!groupId) {
            setSessions([]);
            return;
        }
        try {
            setLoadingSessions(true);
            const params = new URLSearchParams();
            if (groupId) params.append('group_code', groupId.trim());
            if (fromDate) params.append('from_date', fromDate);
            if (toDate) params.append('to_date', toDate);
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/attendance/admin/sessions?${params.toString()}`);
            if (!response.ok) throw new Error('Failed to fetch sessions');
            const data = await response.json();
            
            // Transform to match expected format - include attendance stats from API
            // Ensure all numeric values are properly converted to numbers
            const transformed = data.map(s => ({
                id: s.session_id.toString(),
                courseId: courseId,
                groupId: s.group_code,
                date: s.date,
                start: s.start,
                end: s.end,
                topic: s.topic,
                lecturer: s.lecturer,
                room: s.room,
                // Include attendance stats from API - convert to numbers
                present_count: parseInt(s.present_count, 10) || 0,
                absent_count: parseInt(s.absent_count, 10) || 0,
                late_count: parseInt(s.late_count, 10) || 0,
                excused_count: parseInt(s.excused_count, 10) || 0,
                total_students: parseInt(s.total_students, 10) || 0,
                attendance_percentage: parseFloat(s.attendance_percentage) || 0
            }));
            setSessions(transformed);
            // Preserve selected session if it still exists
            const currentSelected = selectedSessionId;
            if (currentSelected && !transformed.find(s => s.id === currentSelected)) {
                setSelectedSessionId("");
            }
        } catch (error) {
            console.error('Error fetching sessions:', error);
        } finally {
            setLoadingSessions(false);
        }
    };

    // Fetch sessions when group or date range changes
    useEffect(() => {
        refreshSessions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, fromDate, toDate, courseId]);

    // Fetch students for selected group
    useEffect(() => {
        async function fetchStudents() {
            if (!groupId) {
                setStudents([]);
                return;
            }
            try {
                setLoadingStudents(true);
                const response = await fetch(`${process.env.REACT_APP_API_URL}/courses/groups/${encodePathSegment(groupId)}/students`);
                if (!response.ok) throw new Error('Failed to fetch students');
                const data = await response.json();
                
                // Transform to match expected format
                const transformed = data.map(s => ({
                    id: s.student_id.toString(),
                    name: `${s.first_name} ${s.last_name}`,
                    email: s.email
                }));
                setStudents(transformed);
            } catch (error) {
                console.error('Error fetching students:', error);
            } finally {
                setLoadingStudents(false);
            }
        }
        fetchStudents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId]);

    // Fetch attendance when session is selected
    useEffect(() => {
        async function fetchAttendance() {
            if (!selectedSessionId) {
                setAttendanceBySession({});
                return;
            }
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/attendance/admin/session/${selectedSessionId}`);
                if (!response.ok) throw new Error('Failed to fetch attendance');
                const data = await response.json();
                
                // Transform to match expected format
                const attendanceMap = {};
                data.forEach(a => {
                    attendanceMap[a.student_id.toString()] = a.status || 'absent';
                });
                setAttendanceBySession(prev => ({
                    ...prev,
                    [selectedSessionId]: attendanceMap
                }));
            } catch (error) {
                console.error('Error fetching attendance:', error);
            }
        }
        fetchAttendance();
    }, [selectedSessionId]);

    useEffect(() => {
        setSelectedSessionId("");
        setStudentQuery("");
    }, [groupId]);

    const filteredSessions = useMemo(() => {
        const q = sessionQuery.trim().toLowerCase();
        return sessions.filter((s) => {
            // Sessions are already filtered by group_code and date range from API
            if (!q) return true;
            return (
                (s.topic || '').toLowerCase().includes(q) ||
                (s.lecturer || '').toLowerCase().includes(q) ||
                (s.room || '').toLowerCase().includes(q) ||
                (s.date || '').includes(q)
            );
        });
    }, [sessions, sessionQuery]);

    const selectedSession = useMemo(() => {
        if (!selectedSessionId) return null;
        return sessions.find((s) => s.id === selectedSessionId) || null;
    }, [sessions, selectedSessionId]);

    const selectedAttendance = useMemo(() => {
        if (!selectedSessionId) return {};
        return attendanceBySession[selectedSessionId] || {};
    }, [attendanceBySession, selectedSessionId]);

    const visibleStudents = useMemo(() => {
        const q = studentQuery.trim().toLowerCase();
        if (!q) return studentsForGroup;
        return studentsForGroup.filter((s) => s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q));
    }, [studentsForGroup, studentQuery]);

    function computeSessionStats(sessionId) {
        const s = sessions.find((x) => x.id === sessionId);
        if (!s) {
            return { present: 0, absent: 0, late: 0, excused: 0, total: 0, pct: 0 };
        }

        // Use stats from API response - ensure they are numbers, not strings
        return {
            present: parseInt(s.present_count, 10) || 0,
            absent: parseInt(s.absent_count, 10) || 0,
            late: parseInt(s.late_count, 10) || 0,
            excused: parseInt(s.excused_count, 10) || 0,
            total: parseInt(s.total_students, 10) || 0,
            pct: parseFloat(s.attendance_percentage) || 0
        };
    }

    const summaryStats = useMemo(() => {
        let present = 0,
            absent = 0,
            late = 0,
            excused = 0,
            totalMarks = 0;

        filteredSessions.forEach((sess) => {
            const st = computeSessionStats(sess.id);
            // Ensure we're adding numbers, not concatenating strings
            present += Number(st.present) || 0;
            absent += Number(st.absent) || 0;
            late += Number(st.late) || 0;
            excused += Number(st.excused) || 0;
            totalMarks += Number(st.total) || 0;
        });

        const pct = totalMarks ? Math.round((present / totalMarks) * 100) : 0;
        return { 
            present: Number(present), 
            absent: Number(absent), 
            late: Number(late), 
            excused: Number(excused), 
            totalMarks: Number(totalMarks), 
            sessionsCount: filteredSessions.length, 
            pct: Number(pct) 
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredSessions, attendanceBySession, groupId, courseId]);

    async function setStudentStatus(studentId, status) {
        if (!selectedSessionId) return;
        
        // Optimistically update UI
        setAttendanceBySession((prev) => {
            const next = { ...prev };
            const sessionMap = { ...(next[selectedSessionId] || {}) };
            sessionMap[studentId] = status;
            next[selectedSessionId] = sessionMap;
            return next;
        });

        // Save to backend
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/attendance/admin/session/${selectedSessionId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ studentId: parseInt(studentId), status })
            });
            
            if (!response.ok) {
                throw new Error('Failed to save attendance');
            }
            
            // Show success notification
            setNotification({ 
                show: true, 
                message: 'Obecność została zaktualizowana pomyślnie', 
                type: 'success' 
            });
            
            // Refresh sessions to get updated stats
            await refreshSessions();
            
            // Hide notification after 3 seconds
            setTimeout(() => {
                setNotification({ show: false, message: '', type: 'success' });
            }, 3000);
        } catch (error) {
            console.error('Error saving attendance:', error);
            setNotification({ 
                show: true, 
                message: 'Nie udało się zapisać zmian. Spróbuj ponownie.', 
                type: 'error' 
            });
            setTimeout(() => {
                setNotification({ show: false, message: '', type: 'error' });
            }, 3000);
        }
    }

    async function markAll(status) {
        if (!selectedSessionId) return;
        
        // Optimistically update UI
        setAttendanceBySession((prev) => {
            const next = { ...prev };
            const sessionMap = { ...(next[selectedSessionId] || {}) };
            studentsForGroup.forEach((st) => {
                sessionMap[st.id] = status;
            });
            next[selectedSessionId] = sessionMap;
            return next;
        });

        // Save all to backend
        try {
            const promises = studentsForGroup.map(st => 
                fetch(`${process.env.REACT_APP_API_URL}/attendance/admin/session/${selectedSessionId}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ studentId: parseInt(st.id), status })
                })
            );
            
            await Promise.all(promises);
            
            // Show success notification
            const statusLabel = ATTENDANCE_LABEL[status] || status;
            setNotification({ 
                show: true, 
                message: `Wszyscy kursanci zostali oznaczeni jako: ${statusLabel}`, 
                type: 'success' 
            });
            
            // Refresh sessions to get updated stats
            await refreshSessions();
            
            // Hide notification after 3 seconds
            setTimeout(() => {
                setNotification({ show: false, message: '', type: 'success' });
            }, 3000);
        } catch (error) {
            console.error('Error saving attendance:', error);
            setNotification({ 
                show: true, 
                message: 'Nie udało się zapisać zmian. Spróbuj ponownie.', 
                type: 'error' 
            });
            setTimeout(() => {
                setNotification({ show: false, message: '', type: 'error' });
            }, 3000);
        }
    }

    function exportCsv() {
        const courseName = courses.find((c) => c.id === courseId)?.name || "Przedmiot";
        const groupNameRaw = groupsForCourse.find((g) => g.id === groupId)?.name || "Grupa";
        const groupName = groupNameRaw.replaceAll(" ", "_");

        const rows = [];
        rows.push(["Przedmiot", courseName]);
        rows.push(["Grupa", groupNameRaw]);
        rows.push(["Zakres", `${fromDate || "-"} → ${toDate || "-"}`]);
        rows.push([]);
        rows.push(["Data", "Godz.", "Temat", "Prowadzący", "Sala", "Frekwencja %", "Obecni", "Nieobecni", "Spóźnieni", "Usprawiedl."]);

        filteredSessions.forEach((s) => {
            const st = computeSessionStats(s.id);
            rows.push([
                fmtDate(s.date),
                `${s.start}-${s.end}`,
                s.topic || '',
                s.lecturer || '',
                s.room || '',
                `${st.pct}%`,
                st.present,
                st.absent,
                st.late,
                st.excused,
            ]);
        });

        const csv = buildCsv(rows);
        downloadTextFile(`obecnosci_${groupName}.csv`, csv, "text/csv;charset=utf-8");
    }

    return (
        <div className="admin-attendance-page">
            <AdminSidebar />
            
            {/* Notification Toast */}
            {notification.show && (
                <div className={`aa-notification aa-notification--${notification.type}`}>
                    <div className="aa-notification-content">
                        {notification.type === 'success' ? '✓' : '✗'} {notification.message}
                    </div>
                </div>
            )}

            {/* dokładnie jak w Kalendarzu */}
            <div className="main-content">
                <div className="attendance-page-inner">
                    <header className="aa-pageHeader">
                        <div>
                            <h1 className="aa-title">Obecności</h1>
                                <p className="aa-subtitle">Zarządzaj obecnościami kursantów według Przedmiotu, Grupy i Zajęć.</p>
                        </div>

                        <button
                            className="aa-btn aa-btn--outline"
                            type="button"
                            onClick={exportCsv}
                            disabled={!filteredSessions.length}
                        >
                            Export CSV
                        </button>
                    </header>

                    <section className="aa-stats">
                        <div className="aa-statCard">
                            <div className="aa-statLabel">Frekwencja</div>
                            <div className="aa-statValue">{summaryStats.pct}%</div>
                            <div className="aa-statHint">dla przefiltrowanych zajęć</div>
                        </div>

                        <div className="aa-statCard">
                            <div className="aa-statLabel">Zajęcia</div>
                            <div className="aa-statValue">{summaryStats.sessionsCount}</div>
                            <div className="aa-statHint">w zakresie dat</div>
                        </div>

                        <div className="aa-statCard">
                            <div className="aa-statLabel">Nieobecności</div>
                            <div className="aa-statValue aa-red">{summaryStats.absent}</div>
                            <div className="aa-statHint">suma wszystkich wpisów</div>
                        </div>

                        <div className="aa-statCard">
                            <div className="aa-statLabel">Spóźnienia</div>
                            <div className="aa-statValue aa-amber">{summaryStats.late}</div>
                            <div className="aa-statHint">suma wszystkich wpisów</div>
                        </div>
                    </section>

                    <section className="aa-card">
                        <div className="aa-cardHeader">
                            <div>
                                <div className="aa-cardTitle">Lista zajęć</div>
                                <div className="aa-cardDesc">Wybierz Przedmiot i grupę, a następnie kliknij zajęcia, aby edytować obecność.</div>
                            </div>
                        </div>

                        <div className="aa-filters">
                        <div className="aa-field">
                            <label className="aa-label">Przedmiot</label>
                            <select 
                                className="aa-input" 
                                value={courseId} 
                                onChange={(e) => handleCourseChange(e.target.value)}
                                disabled={loading}
                            >
                                <option value="">Wybierz Przedmiot...</option>
                                {courses.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="aa-field">
                            <label className="aa-label">Grupa</label>
                            <select 
                                className="aa-input" 
                                value={groupId} 
                                onChange={(e) => setGroupId(e.target.value)}
                                disabled={loading || !courseId}
                            >
                                <option value="">Wybierz grupę...</option>
                                {groupsForCourse.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                            <div className="aa-field">
                                <label className="aa-label">Od</label>
                                <input className="aa-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                            </div>

                            <div className="aa-field">
                                <label className="aa-label">Do</label>
                                <input className="aa-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                            </div>

                            <div className="aa-field aa-field--grow">
                                <label className="aa-label">Szukaj zajęć</label>
                                <input
                                    className="aa-input"
                                    type="text"
                                    value={sessionQuery}
                                    onChange={(e) => setSessionQuery(e.target.value)}
                                    placeholder="Temat, data, sala, prowadzący..."
                                />
                            </div>
                        </div>

                        <div className="aa-grid">
                            <div className="aa-tableWrap">
                                <table className="aa-table">
                                    <thead>
                                        <tr>
                                            <th>Data</th>
                                            <th>Godzina</th>
                                            <th>Temat</th>
                                            <th>Frekwencja</th>
                                            <th>Akcje</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {loadingSessions ? (
                                            <tr>
                                                <td colSpan={5} className="aa-empty">
                                                    Ładowanie zajęć...
                                                </td>
                                            </tr>
                                        ) : filteredSessions.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="aa-empty">
                                                    Brak zajęć dla wybranych filtrów.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredSessions.map((s) => {
                                                const st = computeSessionStats(s.id);
                                                const isActive = s.id === selectedSessionId;
                                                return (
                                                    <tr
                                                        key={s.id}
                                                        className={isActive ? "aa-row aa-row--active" : "aa-row"}
                                                        onClick={() => setSelectedSessionId(s.id)}
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        <td>{fmtDate(s.date)}</td>
                                                        <td className="aa-mono">
                                                            {s.start}-{s.end}
                                                        </td>
                                                        <td className="aa-topic">{s.topic || 'Brak tytułu'}</td>
                                                        <td>
                                                            <span
                                                                className={
                                                                    st.pct >= 80
                                                                        ? "aa-pill aa-pill--good"
                                                                        : st.pct >= 60
                                                                            ? "aa-pill aa-pill--warn"
                                                                            : "aa-pill aa-pill--bad"
                                                                }
                                                            >
                                                                {st.pct}%
                                                            </span>
                                                            <span className="aa-muted"> ({st.present}/{st.total})</span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="aa-btn aa-btn--tiny"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedSessionId(s.id);
                                                                }}
                                                            >
                                                                Podgląd
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <aside className="aa-details">
                                <div className="aa-detailsHeader">
                                    <div className="aa-detailsTitle">Szczegóły zajęć</div>
                                    <div className="aa-detailsDesc">
                                        {selectedSession ? (
                                            <>
                                                <span className="aa-strong">{fmtDate(selectedSession.date)}</span> · {selectedSession.start}-{selectedSession.end}
                                            </>
                                        ) : (
                                            "Wybierz zajęcia z tabeli"
                                        )}
                                    </div>
                                </div>

                                {selectedSession ? (
                                    <>
                                        <div className="aa-detailsMeta">
                                            <div className="aa-metaRow">
                                                <span className="aa-metaKey">Temat</span>
                                                <span className="aa-metaVal">{selectedSession.topic}</span>
                                            </div>
                                            <div className="aa-metaRow">
                                                <span className="aa-metaKey">Prowadzący</span>
                                                <span className="aa-metaVal">{selectedSession.lecturer}</span>
                                            </div>
                                            <div className="aa-metaRow">
                                                <span className="aa-metaKey">Sala</span>
                                                <span className="aa-metaVal">{selectedSession.room}</span>
                                            </div>
                                        </div>

                                        <div className="aa-actions">
                                            <button className="aa-btn" type="button" onClick={() => markAll(ATTENDANCE.PRESENT)}>
                                                Oznacz wszystkich: Obecny
                                            </button>
                                            <button className="aa-btn aa-btn--outline" type="button" onClick={() => markAll(ATTENDANCE.ABSENT)}>
                                                Oznacz wszystkich: Nieobecny
                                            </button>
                                        </div>

                                        <div className="aa-studentSearch">
                                            <input
                                                className="aa-input"
                                                value={studentQuery}
                                                onChange={(e) => setStudentQuery(e.target.value)}
                                                placeholder="Szukaj kursanta po imieniu lub e-mailu..."
                                            />
                                        </div>

                                        <div className="aa-students">
                                            {loadingStudents ? (
                                                <div className="aa-emptyBox">Ładowanie kursantów...</div>
                                            ) : visibleStudents.length === 0 ? (
                                                <div className="aa-emptyBox">Brak kursantów do wyświetlenia.</div>
                                            ) : (
                                                visibleStudents.map((st) => {
                                                    const status = selectedAttendance[st.id] || ATTENDANCE.ABSENT;
                                                    return (
                                                        <div className="aa-studentRow" key={st.id}>
                                                            <div className="aa-studentInfo">
                                                                <div className="aa-studentName">{st.name}</div>
                                                                <div className="aa-studentEmail">{st.email}</div>
                                                            </div>

                                                            <div className="aa-studentControls">
                                                                <span
                                                                    className={
                                                                        status === ATTENDANCE.PRESENT
                                                                            ? "aa-pill aa-pill--good"
                                                                            : status === ATTENDANCE.LATE
                                                                                ? "aa-pill aa-pill--warn"
                                                                                : status === ATTENDANCE.EXCUSED
                                                                                    ? "aa-pill aa-pill--neutral"
                                                                                    : "aa-pill aa-pill--bad"
                                                                    }
                                                                >
                                                                    {ATTENDANCE_LABEL[status]}
                                                                </span>

                                                                <select
                                                                    className="aa-input aa-input--small"
                                                                    value={status}
                                                                    onChange={(e) => setStudentStatus(st.id, e.target.value)}
                                                                >
                                                                    <option value={ATTENDANCE.PRESENT}>{ATTENDANCE_LABEL[ATTENDANCE.PRESENT]}</option>
                                                                    <option value={ATTENDANCE.ABSENT}>{ATTENDANCE_LABEL[ATTENDANCE.ABSENT]}</option>
                                                                    <option value={ATTENDANCE.LATE}>{ATTENDANCE_LABEL[ATTENDANCE.LATE]}</option>
                                                                    <option value={ATTENDANCE.EXCUSED}>{ATTENDANCE_LABEL[ATTENDANCE.EXCUSED]}</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>

                                    </>
                                ) : (
                                    <div className="aa-emptyBox">Kliknij zajęcia w tabeli, żeby wyświetlić listę kursantów i edytować obecność.</div>
                                )}
                            </aside>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}